import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    landType: '',
    message: '',
    transactionalConsent: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'transactionalConsent' || key === 'marketingConsent') {
          formBody.append(key, value ? 'Yes' : 'No');
        } else {
          formBody.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5rkocetc768qf4bqklg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          landType: '',
          message: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/9d01095367f5138f0dc641415c7fa8ab.jpg"
              alt="Sell your land for cash today"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Ready to Sell Your Land for Cash?
            </h2>
            <p className="text-xl mb-8 text-teal-50 leading-relaxed">
              Stop paying taxes and maintenance on land you don't want. Get your fair cash offer today and close on your timeline. No commissions, no closing costs, no hassles.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl mt-1"></i>
                <div>
                  <strong className="block mb-1">Get Your Offer in 24 Hours</strong>
                  <span className="text-teal-50">Call us today and receive a no-obligation cash offer within 24 hours</span>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl mt-1"></i>
                <div>
                  <strong className="block mb-1">Close on Your Schedule</strong>
                  <span className="text-teal-50">Need to close in 7 days? 30 days? We work around your timeline</span>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl mt-1"></i>
                <div>
                  <strong className="block mb-1">We Handle Everything</strong>
                  <span className="text-teal-50">All paperwork, title work, and closing costs covered by us</span>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="ri-check-line text-2xl mt-1"></i>
                <div>
                  <strong className="block mb-1">Any Condition, Any Situation</strong>
                  <span className="text-teal-50">We buy land with liens, title issues, or any other problems</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <a href="tel:+1-915-206-5814" className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill"></i>
                <span>Call (915) 206-5814</span>
              </a>
              <a href="#process" className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-teal-800 text-white rounded-lg font-bold text-lg hover:bg-teal-900 transition-colors whitespace-nowrap cursor-pointer">
                <span>Learn More</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="pt-8 border-t border-white/20">
              <p className="text-teal-50 text-sm">
                <strong>TRex RE LLC - The Texas Property buyers</strong> has been helping El Paso landowners since 2016. We're not wholesalers or middlemen - we're actual end buyers with the cash and experience to close quickly on any property.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-white rounded-2xl shadow-2xl p-8 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Get Your Cash Offer Today</h3>
          
          {submitStatus === 'success' && (
            <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start space-x-3">
              <i className="ri-checkbox-circle-fill text-teal-600 text-2xl flex-shrink-0"></i>
              <div>
                <div className="font-bold text-teal-900">Thank You!</div>
                <div className="text-teal-700">We'll contact you within 24 hours with your cash offer.</div>
              </div>
            </div>
          )}

          <form id="land-buyers-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(915) 555-0123"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="john@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Property Address *
              </label>
              <input
                type="text"
                name="propertyAddress"
                required
                value={formData.propertyAddress}
                onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="123 Main St, El Paso, TX or County/Parcel Number"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Land Type *
              </label>
              <select
                name="landType"
                required
                value={formData.landType}
                onChange={(e) => setFormData({...formData, landType: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select land type</option>
                <option value="Residential Lot">Residential Lot</option>
                <option value="Commercial Land">Commercial Land</option>
                <option value="Agricultural/Farm">Agricultural/Farm Land</option>
                <option value="Vacant Land">Vacant Land</option>
                <option value="Rural/Ranch">Rural/Ranch Land</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell us about your land, size, access, utilities, etc. (500 characters max)"
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.message.length}/500 characters
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="land-transactionalConsent"
                  name="transactionalConsent"
                  required
                  checked={formData.transactionalConsent}
                  onChange={(e) => setFormData({ ...formData, transactionalConsent: e.target.checked })}
                  className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="land-transactionalConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                  <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="land-marketingConsent"
                  name="marketingConsent"
                  checked={formData.marketingConsent}
                  onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                  className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="land-marketingConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                  I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg font-bold text-lg hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-send-plane-fill mr-2"></i>
                  Get My Cash Offer
                </span>
              )}
            </button>

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                Something went wrong. Please call us at (915) 206-5814.
              </div>
            )}

            <p className="text-xs text-gray-500 text-center">
              By submitting this form, you agree to be contacted about your property. We respect your privacy and will never share your information.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
